import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { useEffect, useRef, useState } from 'react';

import styles from './ExcoPlayer.module.scss';

declare global {
  interface Window {
    ExCoPlayer?: {
      connect: (playerId?: string) => ExCoPlayerApi;
    };
  }
}

type ExCoPlayerApi = {
  init: (options: { sticky: object; variation: string }) => Promise<void>;
  destroy: () => void;
  setStickyState: (state: boolean) => void;
};

const SCRIPT_ID = 'exco-player-script';
const PLAYER_ID = '34c93dc6-f9c2-4ad1-85ce-def4a929874b';

const loadExcoScript = (playerId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      return resolve();
    }

    const script = document.createElement('script');

    script.src = `//player.ex.co/player/${playerId}`;
    script.async = true;
    script.id = SCRIPT_ID;
    script.className = 'exco-player';
    script.setAttribute('programmatic', 'true');

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load ExcoPlayer script'));

    document.body.appendChild(script);
  });
};

export const ExcoPlayer = () => {
  const { user } = useAuth();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const playerApi = useRef<ExCoPlayerApi | null>(null);

  const isPremiumUser = user?.st?.includes('premium') || false;

  useEffect(() => {
    return () => {
      playerApi.current?.destroy();
    };
  }, [playerApi]);

  // Load the script only once
  useEffect(() => {
    if (isPremiumUser) return;

    loadExcoScript(PLAYER_ID)
      .then(() => {
        setIsScriptLoaded(true);
      })
      .catch((error) => {
        console.error('ExcoPlayer script failed to load:', error);
      });
  }, [isPremiumUser]);

  // Re-init the player on route change
  useEffect(() => {
    if (!isScriptLoaded || isPremiumUser) return;

    const initPlayer = () => {
      if (!window.ExCoPlayer) {
        console.warn('ExCoPlayer is not available yet');

        return;
      }

      playerApi.current = window.ExCoPlayer.connect(PLAYER_ID);

      if (!playerApi.current) return;

      playerApi.current
        .init({
          sticky: {
            mode: user?.has ? 'custom' : 'non-persistent-in-view',
          },
          variation: user?.has ? 'subscriber' : 'guest',
        })
        .then(() => {
          if (user?.has) {
            playerApi.current?.setStickyState(false);
          }
        })
        .catch((err) => {
          console.error('Failed to init player:', err);
        });
    };

    initPlayer();
  }, [isScriptLoaded, user?.has, isPremiumUser]);

  if (isPremiumUser) return null;

  return (
    <div className={styles.container}>
      <div id={PLAYER_ID} />
    </div>
  );
};
